import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getErpEndpointFormConfig } from '../../configs/erpEndpointFormConfig';
import { erpEndpointsService } from '../../api/erpEndpointsService';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import { parseJsonField } from '../../utils/parseJsonField';
import type { CreateErpEndpointPayload, UpdateErpEndpointPayload } from '../../api/erpEndpointsService';

interface EndpointParams {
  registrySlug?: string;
  endpointSlug?: string;
}

const JSON_FIELDS = [
  'path_parameters',
  'query_parameters',
  'headers',
  'request_schema',
  'request_transform',
  'response_schema',
  'response_transform',
  'example_params',
] as const;

export function EndpointFormPage() {
  const { registrySlug } = useParams<{ registrySlug: string }>();

  /* v8 ignore start */
  const registryHref = `/admin/erp/registries/${registrySlug ?? ''}`;
  /* v8 ignore stop */

  // ---- Fetch registry name for breadcrumb ----------------------------------

  const { data: registryResponse } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registrySlug],
    queryFn: () => {
      /* v8 ignore start */
      if (!registrySlug) return Promise.reject(new Error('Registry slug is required'));
      /* v8 ignore stop */
      return erpRegistriesService.get(registrySlug);
    },
    enabled: !!registrySlug,
  });

  const registryName = registryResponse?.data?.name ?? 'Registry';

  // ---- Shared form page hook -----------------------------------------------

  const formPage = useAdminFormPage<unknown, EndpointParams>({
    entityName: 'endpoint',
    getConfig: (mode, params) =>
      getErpEndpointFormConfig(mode, params.registrySlug ?? '', params.endpointSlug),
    getEditId: (params) => params.endpointSlug,
    queryKey: (params) => [
      'admin', 'erp', 'registries', params.registrySlug, 'endpoints', params.endpointSlug,
    ],
    fetchFn: (params) => {
      if (!params.registrySlug || !params.endpointSlug) {
        return Promise.reject(new Error('Registry slug and Endpoint slug are required'));
      }
      return erpEndpointsService.get(params.registrySlug, params.endpointSlug);
    },
    fetchEnabled: (isEdit, params) => isEdit && !!params.registrySlug,
    mapToInitialValues: (entity) => {
      const ep = entity as Record<string, unknown>;
      return {
        slug: ep.slug,
        name: ep.name,
        description: (ep.description as string) ?? '',
        http_method: ep.http_method,
        path: ep.path,
        path_parameters: ep.path_parameters ? JSON.stringify(ep.path_parameters, null, 2) : '',
        query_parameters: ep.query_parameters ? JSON.stringify(ep.query_parameters, null, 2) : '',
        headers: ep.headers ? JSON.stringify(ep.headers, null, 2) : '',
        request_schema: ep.request_schema ? JSON.stringify(ep.request_schema, null, 2) : '',
        request_transform: ep.request_transform ? JSON.stringify(ep.request_transform, null, 2) : '',
        response_schema: ep.response_schema ? JSON.stringify(ep.response_schema, null, 2) : '',
        response_transform: ep.response_transform ? JSON.stringify(ep.response_transform, null, 2) : '',
        example_params: ep.example_params ? JSON.stringify(ep.example_params, null, 2) : '',
        timeout_ms: (ep.timeout_ms as number | null) ?? '',
        is_active: ep.is_active,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const cleanedPayload: Record<string, unknown> = { ...payload };

      for (const field of JSON_FIELDS) {
        cleanedPayload[field] = parseJsonField(payload[field]);
      }

      cleanedPayload.description = (payload.description as string) || null;
      cleanedPayload.timeout_ms = payload.timeout_ms ? Number(payload.timeout_ms) : null;

      if (isEdit && params.registrySlug && params.endpointSlug) {
        return erpEndpointsService.update(
          params.registrySlug,
          params.endpointSlug,
          cleanedPayload as UpdateErpEndpointPayload,
        );
      }
      if (params.registrySlug) {
        const registryUuid = registryResponse?.data?.id;
        if (registryUuid) {
          cleanedPayload.erp_registry_id = registryUuid;
        }
        return erpEndpointsService.create(
          params.registrySlug,
          cleanedPayload as unknown as CreateErpEndpointPayload,
        );
      }
      throw new Error('Registry slug is required');
    },
    invalidateKey: (params) => [
      'admin', 'erp', 'registries', params.registrySlug, 'endpoints',
    ],
    navigateBackPath: (params) => `/admin/erp/registries/${params.registrySlug ?? ''}`,
  });

  // ---- Breadcrumbs ---------------------------------------------------------

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: registryName, href: registryHref },
    { label: formPage.isEdit ? 'Edit Endpoint' : 'Create Endpoint' },
  ], [formPage.isEdit, registryName, registryHref]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: registryName, href: registryHref },
    { label: 'Edit Endpoint' },
  ], [registryName, registryHref]);

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Endpoint"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred."
    />
  );
}
